import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Stack,
    Avatar, Typography, IconButton, Chip, InputBase, CircularProgress, Select, MenuItem,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddAPhotoRoundedIcon from '@mui/icons-material/AddAPhotoRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import http, { apiErrorMessage } from '../../Api/http';
import { CreateGroup, GetChatUsers, SearchUsers, GetUserTypes } from '../../Api/Api';
import { solidBtn, ghostBtn, field } from '../uiKit';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { avatarToneFor, initialsFrom } from './chatHelpers';
import { selectIsMasterAdmin } from '../../redux/slices/authSlice';
import { selectActiveEntityId } from '../../redux/slices/orgSlice';

// Doc §4.1/§4.3: `EntityId` (CreateGroup body) / `entityId` (SearchUsers,
// GetChatUsers query) is required for MasterAdmin only — everyone else
// always acts inside their own entity server-side. Only send it once it's a
// real numeric backend entity id (mirrors the same numeric guard http.js's
// own X-Entity-Id header uses) — the app's entity picker can also hold the
// "all entities" sentinel, which isn't a valid single EntityId to send here.
const numericEntityId = (id) => (/^\d+$/.test(String(id ?? '')) ? String(id) : null);

export default function CreateGroupDialog({ open, onClose, onCreated, onForbidden }) {
    const isMasterAdmin = useSelector(selectIsMasterAdmin);
    const activeEntityId = useSelector(selectActiveEntityId);
    const entityIdForRequest = isMasterAdmin ? numericEntityId(activeEntityId) : null;
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [icon, setIcon] = useState(null);
    const [iconPreview, setIconPreview] = useState('');
    const iconRef = useRef(null);

    const [query, setQuery] = useState('');
    const [directory, setDirectory] = useState([]);
    // The full, unfiltered directory (fetched once per open) — used only to
    // derive the "User Type" dropdown's options, so the options list doesn't
    // shrink to just the currently-selected type once a filter is applied.
    const [allUsers, setAllUsers] = useState([]);
    // The doc doesn't enumerate a fixed role list for the member picker's
    // `userTypeId` filter — it just takes a role id, assuming the app already
    // has a role catalogue. Deriving options only from whichever users are
    // already loaded would silently drop any role with zero chat-enabled
    // members so far, so fetch the entity's actual role catalogue instead —
    // the same GetUserTypes source RolesPage.jsx and ChatSettingsPage.jsx use
    // — and fall back to the loaded-user-derived list only if that call fails.
    const [roleCatalog, setRoleCatalog] = useState([]);
    const [userTypeFilter, setUserTypeFilter] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const searchTimer = useRef(null);

    const reset = () => {
        setName(''); setDescription(''); setIcon(null); setIconPreview('');
        setQuery(''); setUserTypeFilter(''); setSelected([]); setSaving(false);
    };

    useEffect(() => {
        if (!open) { setRoleCatalog([]); return; }
        http.get(GetUserTypes).then(({ data: body }) => {
            if (body?.error) return;
            setRoleCatalog(Array.isArray(body?.data?.roles) ? body.data.roles : []);
        }).catch(() => {});
    }, [open]);

    const userTypeOptions = useMemo(() => {
        if (roleCatalog.length) return roleCatalog.map((r) => ({ id: r.id, label: r.name }));
        const seen = new Map();
        allUsers.forEach((u) => {
            const id = u.userTypeId ?? u.userType;
            const label = u.userType || u.role;
            if (id != null && label && !seen.has(String(id))) seen.set(String(id), label);
        });
        return Array.from(seen, ([id, label]) => ({ id, label }));
    }, [roleCatalog, allUsers]);

    // Doc §4.1: only SearchUsers (query?, userTypeId?, entityId?) is
    // documented to accept a `userTypeId` filter — GetChatUsers only takes
    // `entityId?`. `query` on SearchUsers is itself optional, so the "User
    // Type" dropdown routes through SearchUsers (with query omitted) whenever
    // a type is picked, even with no search text typed; GetChatUsers is only
    // used for the true "no filters at all" directory load.
    const fetchDirectory = (q) => {
        const entityParams = entityIdForRequest ? { entityId: entityIdForRequest } : {};
        const req = (q || userTypeFilter)
            ? http.get(SearchUsers, { params: { ...entityParams, ...(q ? { query: q } : {}), ...(userTypeFilter ? { userTypeId: userTypeFilter } : {}) } })
            : http.get(GetChatUsers, { params: entityParams });
        return req.then(({ data: body }) => {
            if (body?.error) throw new Error(body.message);
            const list = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
            setDirectory(list);
            if (!q && !userTypeFilter) setAllUsers(list);
        });
    };

    useEffect(() => {
        if (!open) { reset(); return; }
        setLoadingUsers(true);
        fetchDirectory('').catch(() => setDirectory([])).finally(() => setLoadingUsers(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const q = query.trim();
        const run = () => {
            setLoadingUsers(true);
            fetchDirectory(q).catch(() => {}).finally(() => setLoadingUsers(false));
        };
        if (!q) { run(); return undefined; }
        searchTimer.current = setTimeout(run, 300);
        return () => clearTimeout(searchTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, userTypeFilter]);

    const toggleUser = (u) => {
        const id = u.userId ?? u.id;
        setSelected((list) => (list.some((s) => (s.userId ?? s.id) === id) ? list.filter((s) => (s.userId ?? s.id) !== id) : [...list, u]));
    };

    const pickIcon = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setIcon(f);
        setIconPreview(URL.createObjectURL(f));
    };

    const submit = async () => {
        if (!name.trim()) { toast.error('Give the group a name.'); return; }
        if (selected.length === 0) { toast.error('Add at least one member.'); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('Name', name.trim());
            if (description.trim()) fd.append('Description', description.trim());
            if (icon) fd.append('IconFile', icon);
            fd.append('MemberUserIds', selected.map((u) => u.userId ?? u.id).join(','));
            if (entityIdForRequest) fd.append('EntityId', entityIdForRequest);
            const { data: body } = await http.post(CreateGroup, fd);
            if (body?.error) throw new Error(body.message || 'Could not create the group.');
            toast.success(body?.message || 'Group created.');
            onCreated?.(body?.data);
            onClose();
        } catch (err) {
            // Doc §4.3: creating a group is gated by the create-group
            // permission — a 403 here means the effective setting for this
            // role is "not allowed," so hide the New Group button going
            // forward (the doc's try-and-403 fallback, since there's no
            // dedicated "can I create" GET for a normal user).
            if (err?.response?.status === 403) onForbidden?.();
            toast.error(apiErrorMessage(err, 'Could not create the group.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A', flex: 1 }}>New group</Typography>
                <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '4px !important' }}>
                <Stack spacing={1.6}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                        <input ref={iconRef} type="file" hidden accept="image/*" onChange={pickIcon} />
                        <Box
                            onClick={() => iconRef.current?.click()}
                            sx={{ width: 54, height: 54, borderRadius: '12px', flexShrink: 0, cursor: 'pointer', bgcolor: PRIMARY_LIGHT, border: `1px dashed ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundImage: iconPreview ? `url(${iconPreview})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }}
                        >
                            {!iconPreview && <AddAPhotoRoundedIcon sx={{ fontSize: 20, color: PRIMARY }} />}
                        </Box>
                        <TextField label="Group name" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={field} />
                    </Stack>
                    <TextField label="Description (optional)" size="small" fullWidth multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} sx={field} />

                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>User type</Typography>
                        <Select
                            size="small"
                            fullWidth
                            displayEmpty
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value)}
                            sx={{ ...field['& .MuiOutlinedInput-root'], fontSize: 13 }}
                        >
                            <MenuItem value="" sx={{ fontSize: 13 }}>All members</MenuItem>
                            {userTypeOptions.map((t) => (
                                <MenuItem key={t.id} value={t.id} sx={{ fontSize: 13 }}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {selected.length > 0 && (
                        <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', gap: 0.6 }}>
                            {selected.map((u) => (
                                <Chip key={u.userId ?? u.id} label={u.name || u.userName} onDelete={() => toggleUser(u)} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700 }} />
                            ))}
                        </Stack>
                    )}

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '9px', px: 1.4, height: 38 }}>
                        <SearchRoundedIcon sx={{ fontSize: 17, color: '#98A0AE' }} />
                        <InputBase placeholder="Search people…" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                    </Stack>

                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            Staff ({directory.length})
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: PRIMARY }}>
                            {selected.length} selected
                        </Typography>
                    </Stack>

                    <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #EEF0F6', borderRadius: '9px' }}>
                        {loadingUsers ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} /></Box>
                        ) : directory.length === 0 ? (
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE', textAlign: 'center', py: 3 }}>No users found.</Typography>
                        ) : (
                            directory.map((u) => {
                                const id = u.userId ?? u.id;
                                const on = selected.some((s) => (s.userId ?? s.id) === id);
                                const tone = avatarToneFor(u.name || u.userName);
                                return (
                                    <Stack key={id} direction="row" spacing={1.2} onClick={() => toggleUser(u)} sx={{ alignItems: 'center', px: 1.4, py: 0.9, cursor: 'pointer', borderBottom: '1px solid #F4F5F9', '&:hover': { bgcolor: '#F8F8FC' } }}>
                                        <Avatar sx={{ width: 30, height: 30, ...tone, fontSize: 11, fontWeight: 800 }}>{initialsFrom(u.name || u.userName)}</Avatar>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{u.name || u.userName}</Typography>
                                            {u.designation && <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{u.designation}</Typography>}
                                        </Box>
                                        {on && (
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <CheckRoundedIcon sx={{ fontSize: 13, color: '#fff' }} />
                                            </Box>
                                        )}
                                    </Stack>
                                );
                            })
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                <Button onClick={submit} disabled={saving} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                    {saving ? 'Creating…' : 'Create group'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
